import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useAnimatedCounter } from '../../hooks/useAnimatedCounter';

interface MetricCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: LucideIcon;
    trend?: {
        value: number;
        direction: 'up' | 'down';
    };
    color?: 'ember' | 'safety' | 'alert' | 'steel';
    delay?: number;
}

const colorClasses = {
    ember: {
        bg: 'from-ember-500 to-ember-600',
        shadow: 'shadow-ember',
        ring: 'ring-ember-500/20',
    },
    safety: {
        bg: 'from-safety-500 to-safety-600',
        shadow: 'shadow-safety',
        ring: 'ring-safety-500/20',
    },
    alert: {
        bg: 'from-alert-500 to-alert-600',
        shadow: 'shadow-alert',
        ring: 'ring-alert-500/20',
    },
    steel: {
        bg: 'from-steel-500 to-steel-600',
        shadow: 'shadow-md',
        ring: 'ring-steel-500/20',
    },
};

export default function MetricCard({
    title,
    value,
    subtitle,
    icon: Icon,
    trend,
    color = 'ember',
    delay = 0,
}: MetricCardProps) {
    const colors = colorClasses[color];

    // Scroll animation - trigger when card enters viewport
    const { ref, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });

    // Animated counter for numeric values
    const numericValue = typeof value === 'number' ? value : parseFloat(String(value));
    const isNumeric = !isNaN(numericValue);
    const { displayValue } = useAnimatedCounter(
        isNumeric ? numericValue : 0,
        { isInView, duration: 2000 }
    );

    return (
        <div
            ref={ref}
            className={`card-terminal p-6 hover:border-[var(--fire-orange)] transition-all duration-300 group cursor-pointer gpu-accelerated ${isInView ? 'animate-fadeInUp opacity-100' : 'opacity-0'
                }`}
            style={{ animationDelay: `${delay}ms` }}
            role="article"
            aria-label={`${title}: ${value}`}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-400 mb-1">{title}</p>
                    <h3 className="text-3xl font-display font-bold text-white group-hover:scale-105 transition-transform origin-left">
                        {isNumeric ? displayValue : value}
                    </h3>
                    {subtitle && (
                        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                    )}
                </div>
                <div className={`w-12 h-12 bg-gradient-to-br ${colors.bg} rounded-xl ${colors.shadow} flex items-center justify-center group-hover:scale-110 transition-transform animate-float`}>
                    <Icon className="w-6 h-6 text-white" aria-hidden="true" />
                </div>
            </div>

            {trend && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--blueprint-line)]">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${trend.direction === 'up'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                        }`}>
                        {trend.direction === 'up' ? (
                            <TrendingUp className="w-3 h-3" aria-hidden="true" />
                        ) : (
                            <TrendingDown className="w-3 h-3" aria-hidden="true" />
                        )}
                        <span>{Math.abs(trend.value)}%</span>
                    </div>
                    <span className="text-xs text-gray-500">vs last month</span>
                </div>
            )}
        </div>
    );
}
